import { db } from "@/lib/db";
import { trackEvent } from "./trackEvent";

export interface ClarityMetrics {
  sessionId: string;
  profileId: string;
  userId: string;
  clarityScore?: number; // 1-10 scale
  outcomeAchieved?: boolean;
  outcomeDescription?: string;
  reflectionCount: number;
  insightsGenerated: number;
  actionsTaken: number;
  sessionDuration: number; // milliseconds
  messagesExchanged: number;
  topicsDiscussed: string[];
  emotionalState?: "overwhelmed" | "stuck" | "scattered" | "clear" | "confident" | "uncertain";
  beforeState?: string;
  afterState?: string;
}

/**
 * Track a clarity/outcome metric
 */
export async function trackClarityMetric(
  profileId: string,
  userId: string,
  metrics: Partial<ClarityMetrics>
): Promise<void> {
  try {
    const sessionId = metrics.sessionId || crypto.randomUUID();
    
    // Store in analytics events
    await trackEvent(profileId, "clarity_metric", {
      sessionId,
      userId,
      ...metrics,
      timestamp: Date.now(),
    });
    
    // Also store in a dedicated clarity metrics table if it exists
    if (db.clarityMetrics) {
      const clarityMetric: ClarityMetrics = {
        sessionId,
        profileId,
        userId,
        reflectionCount: metrics.reflectionCount || 0,
        insightsGenerated: metrics.insightsGenerated || 0,
        actionsTaken: metrics.actionsTaken || 0,
        sessionDuration: metrics.sessionDuration || 0,
        messagesExchanged: metrics.messagesExchanged || 0,
        topicsDiscussed: metrics.topicsDiscussed || [],
        ...metrics,
      };
      
      // Use put() instead of add() to update if exists, insert if not
      // This prevents ConstraintError when the same sessionId is tracked multiple times
      await db.clarityMetrics.put(clarityMetric);
    }
  } catch (error) {
    // Handle ConstraintError gracefully - this can happen if the same sessionId is tracked multiple times
    if (error instanceof Error && error.name === "ConstraintError") {
      console.warn("Clarity metric already exists for this session, skipping duplicate:", error);
      // Try to update instead
      try {
        if (db.clarityMetrics && metrics.sessionId) {
          await db.clarityMetrics.put({
            sessionId: metrics.sessionId,
            profileId,
            userId,
            reflectionCount: metrics.reflectionCount || 0,
            insightsGenerated: metrics.insightsGenerated || 0,
            actionsTaken: metrics.actionsTaken || 0,
            sessionDuration: metrics.sessionDuration || 0,
            messagesExchanged: metrics.messagesExchanged || 0,
            topicsDiscussed: metrics.topicsDiscussed || [],
            ...metrics,
          });
        }
      } catch (updateError) {
        console.error("Failed to update clarity metric:", updateError);
      }
    } else {
      console.error("Failed to track clarity metric:", error);
    }
  }
}

/**
 * Track a reflection session
 */
export async function trackReflection(
  profileId: string,
  userId: string,
  reflection: {
    question: string;
    answer: string;
    clarityGained?: number; // 1-10
    insight?: string;
  }
): Promise<void> {
  try {
    await trackEvent(profileId, "reflection", {
      userId,
      question: reflection.question,
      answer: reflection.answer,
      clarityGained: reflection.clarityGained,
      insight: reflection.insight,
      timestamp: Date.now(),
    });
  } catch (error) {
    console.error("Failed to track reflection:", error);
  }
}

/**
 * Track an actionable insight
 */
export async function trackInsight(
  profileId: string,
  userId: string,
  insight: {
    type: "decision" | "plan" | "realization" | "next_step";
    content: string;
    actionable: boolean;
    confidence?: number; // 1-10
  }
): Promise<void> {
  try {
    await trackEvent(profileId, "insight", {
      userId,
      type: insight.type,
      content: insight.content,
      actionable: insight.actionable,
      confidence: insight.confidence,
      timestamp: Date.now(),
    });
  } catch (error) {
    console.error("Failed to track insight:", error);
  }
}

/**
 * Track outcome achievement
 */
export async function trackOutcome(
  profileId: string,
  userId: string,
  outcome: {
    achieved: boolean;
    description: string;
    clarityScoreBefore?: number;
    clarityScoreAfter?: number;
    timeToClarity?: number; // milliseconds
  }
): Promise<void> {
  try {
    await trackEvent(profileId, "outcome", {
      userId,
      achieved: outcome.achieved,
      description: outcome.description,
      clarityScoreBefore: outcome.clarityScoreBefore,
      clarityScoreAfter: outcome.clarityScoreAfter,
      timeToClarity: outcome.timeToClarity,
      timestamp: Date.now(),
    });
  } catch (error) {
    console.error("Failed to track outcome:", error);
  }
}

/**
 * Get clarity metrics for a user
 */
export async function getClarityMetrics(
  profileId: string,
  userId: string,
  sessionId?: string
): Promise<ClarityMetrics[]> {
  try {
    if (db.clarityMetrics) {
      let query = db.clarityMetrics
        .where("profileId")
        .equals(profileId)
        .and((m) => m.userId === userId);
      
      if (sessionId) {
        // Type assertion needed for Dexie query chaining
        query = query.and((m) => m.sessionId === sessionId) as typeof query;
      }
      
      return await query.toArray();
    }
    
    // Fallback: get from analytics events
    const events = await db.analyticsEvents
      .where("profileId")
      .equals(profileId)
      .and((e) => e.metadata?.userId === userId)
      .and((e) => e.eventType === "clarity_metric")
      .toArray();
    
    return events.map((e) => e.metadata as ClarityMetrics);
  } catch (error) {
    console.error("Failed to get clarity metrics:", error);
    return [];
  }
}

